export function selectionFromTextarea(input: HTMLTextAreaElement): { anchor: number; focus: number } {
  if (input.selectionDirection === "backward") {
    return { anchor: input.selectionEnd, focus: input.selectionStart };
  }

  return { anchor: input.selectionStart, focus: input.selectionEnd };
}

export function isArrowKey(key: string): key is "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown" {
  return key === "ArrowLeft" || key === "ArrowRight" || key === "ArrowUp" || key === "ArrowDown";
}

export function isModKey(event: React.KeyboardEvent<HTMLTextAreaElement>): boolean {
  return event.metaKey || event.ctrlKey;
}

export function hasTextRange(selection: { anchor: number; focus: number }): boolean {
  return selection.anchor !== selection.focus;
}

export function normalizedTextRange(selection: { anchor: number; focus: number }): { start: number; end: number } {
  return {
    start: Math.min(selection.anchor, selection.focus),
    end: Math.max(selection.anchor, selection.focus),
  };
}

export function commandArrowSelection(
  input: HTMLTextAreaElement,
  key: "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown",
  extend: boolean,
): { anchor: number; focus: number } {
  const current = selectionFromTextarea(input);
  const focus = current.focus;
  const target = key === "ArrowUp"
    ? 0
    : key === "ArrowDown"
      ? input.value.length
      : key === "ArrowLeft"
        ? lineStartIndex(input.value, focus)
        : lineEndIndex(input.value, focus);

  return extend ? { anchor: current.anchor, focus: target } : { anchor: target, focus: target };
}

export function textWithDeletedSelection(value: string, selectionStart: number, selectionEnd: number): { value: string; cursor: number } {
  const start = Math.min(selectionStart, selectionEnd);
  const end = Math.max(selectionStart, selectionEnd);
  return {
    value: `${value.slice(0, start)}${value.slice(end)}`,
    cursor: start,
  };
}

export function setTextareaSelection(input: HTMLTextAreaElement | null, anchor: number, focus: number): void {
  if (!input) {
    return;
  }

  if (focus < anchor) {
    input.setSelectionRange(focus, anchor, "backward");
    return;
  }

  input.setSelectionRange(anchor, focus, "forward");
}

function lineStartIndex(value: string, index: number): number {
  return value.lastIndexOf("\n", Math.max(0, index - 1)) + 1;
}

function lineEndIndex(value: string, index: number): number {
  const nextBreak = value.indexOf("\n", index);
  return nextBreak === -1 ? value.length : nextBreak;
}

function isTextWhitespace(character: string): boolean {
  return /\s/u.test(character);
}

export function wordBoundariesAtIndex(characters: string, index: number): { start: number; end: number } {
  if (characters.length === 0) {
    return { start: 0, end: 0 };
  }

  const safeIndex = Math.max(0, Math.min(index, characters.length));
  let probe = safeIndex;
  if (probe >= characters.length || isTextWhitespace(characters[probe]!)) {
    probe = Math.max(0, probe - 1);
  }

  if (probe < 0 || isTextWhitespace(characters[probe]!)) {
    return { start: safeIndex, end: safeIndex };
  }

  let start = probe;
  while (start > 0 && !isTextWhitespace(characters[start - 1]!)) {
    start -= 1;
  }

  let end = probe + 1;
  while (end < characters.length && !isTextWhitespace(characters[end]!)) {
    end += 1;
  }

  return { start, end };
}
